import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';

import {
	AudioTechnology,
	MonetizationType,
	ObjectType,
	Offer,
	PresentationType,
	VideoTechnology,
} from '@/@types/graphql-types';
import { TitleDetail, TitleDetailsNode } from '@/interfaces/title-details-graphql';
import { ImpressionTrackingEvents } from '@/enums/events';

import { subscriptionPricesUS, subscriptionPricesUK } from '@/components/buybox-new/new-buybox-config';

import { useI18n } from '@/helpers/composables/useI18n';
import type { UsePromotion } from '@/helpers/composables/usePromotion';
import { usePromotion } from '@/helpers/composables/usePromotion';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { useConstantStore, useLanguageStore } from '@/helpers/composables/useStores';
import { BuyboxParam, OfferLocation } from '@/helpers/offer-helper';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { FALLBACK_PROMOTION_OFFER, geoBlockedCountriesFreeTrialAppleFallback } from '@/helpers/promotion-helper';
import { getOfferImpressionContext, observableOptions } from '@/helpers/composables/useOfferImpressionTracking';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SnowplowClickoutContextGraphql } from '@/helpers/tracking/providers';


export default /*#__PURE__*/_defineComponent({
  __name: 'BuyBoxFreeTrialNew',
  props: {
    offers: { default: () => [] },
    title: null
  },
  emits: ["clickOut"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'clickOut',
		payload: { buyboxOffer: UsePromotion['promotionOffer']; offerLocation: OfferLocation.FREE_TRIAL }
	): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { offers: Offer[], title: TitleDetail }

const ProviderIcon = () => import('@/components/picture/ProviderIcon.vue');

const { t, tc } = useI18n();

const { country } = useLanguageStore();
const { allProvidersById } = useConstantStore();





const { isArbitrating, withArbitrageTracking, trackTagEvent } = useArbitrage();

const { inPromotionCatalogue, promotionOffer, catalogueItem, promotionCatalogue } = usePromotion({
	title: () => props.title,
	offers: () => props.offers,
});

const playerIcon = `/${ASSETS_DIR}/img/justwatch-round-icon-play.svg`;

const mainText = computed(() =>
	catalogueItem.value?.type === 'PROMOTION' && promotionOffer?.value
		? t('WEBAPP_GET_PROVIDER_X', { provider: promotionOffer?.value?.package.clearName })
		: tc('WEBAPP_STREAM_WITH_FREE_TRIAL')
);

const subText = computed(() => {
	if (!catalogueItem.value || !promotionOffer?.value) return '';

	const provider = promotionOffer?.value?.package.clearName;

	if (inPromotionCatalogue.value) {
		return catalogueItem.value.type === 'PROMOTION'
			? t('WEBAPP_WATCH_ON_PROVIDER_X', { provider })
			: t('WEBAPP_FREE_TRIAL_TEXT', { provider });
	}

	if (catalogueItem.value.type === 'PROMOTION') {
		return props.title.objectType === ObjectType.Movie
			? t('WEBAPP_WATCH_SIMILAR_MOVIES_ON_X', { provider })
			: t('WEBAPP_WATCH_SIMILAR_TV_SHOWS_ON_X', { provider });
	}

	return props.title.objectType === ObjectType.Movie
		? t('WEBAPP_WATCH_SIMILAR_MOVIES_FREE_ON_X', { provider })
		: t('WEBAPP_WATCH_SIMILAR_TV_SHOWS_FREE_ON_X', { provider });
});

const freeTrialText = computed(() => {
	let provider;

	if (country.value === 'US') {
		provider = subscriptionPricesUS.find(provider => {
			return provider.id === promotionOffer?.value?.package.packageId;
		});
	} else if (country.value === 'GB') {
		provider = subscriptionPricesUK.find(provider => {
			return provider.id === promotionOffer?.value?.package.packageId;
		});
	}

	return provider?.free_trial;
});

const clickoutLink = computed(() => {
	if (!promotionOffer?.value) return;

	if (promotionOffer?.value?.package.packageId !== 350 && promotionOffer?.value.id === FALLBACK_PROMOTION_OFFER) {
		return promotionOffer?.value.standardWebURL;
	}

	const clickoutUrl = new ClickoutUrl(promotionOffer?.value).set('uct_country', country.value);

	clickoutUrl.set('uct_buybox', BuyboxParam.BuyBoxExp4Variant2);

	if (isArbitrating.value) {
		return withArbitrageTracking(clickoutUrl, promotionOffer).toString();
	}

	const hasAppleTvPlusOffer = props.offers?.some(offer => offer?.package.packageId === 350);
	const hasAppleCrunchRollOffer = props.offers?.some(offer => offer?.package.packageId === 283);
	const packageId = promotionOffer?.value?.package.packageId + '';

	const conversionTrackingByProviderId = {
		350: hasAppleTvPlusOffer ? 'atds' : 'natds',
		283: hasAppleCrunchRollOffer ? 'free_trial' : null,
	};

	return clickoutUrl
		.set(
			'uct_ct',
			conversionTrackingByProviderId[packageId as unknown as keyof typeof conversionTrackingByProviderId] ??
				conversionTrackingByProviderId['350']
		)
		.set('uct_itsct', hasAppleTvPlusOffer ? 'justwatch_tv_5' : 'justwatch_tv_6')
		.toString();
});

const packageImg = computed(() => {
	let iconFilename = '';

	if (country.value === 'US') {
		iconFilename =
			subscriptionPricesUS.find(provider => {
				return provider?.id === promotionOffer?.value?.package.packageId;
			})?.provider_icon ?? '';
	} else if (country.value === 'GB') {
		iconFilename =
			subscriptionPricesUK.find(provider => {
				return provider?.id === promotionOffer?.value?.package.packageId;
			})?.provider_icon ?? '';
	}

	return iconFilename ? `https://www.justwatch.com/appassets/img/rectangular-provider-icons/${iconFilename}` : null;
});

const ageRating = computed(() => {
	// @ts-ignore
	return props.title.content.ageCertification;
});

const retailPrice = computed(() => {
	let provider;

	if (country.value === 'US') {
		provider = subscriptionPricesUS.find(provider => {
			return provider.id === promotionOffer?.value?.package.packageId;
		});
	} else if (country.value === 'GB') {
		provider = subscriptionPricesUK.find(provider => {
			return provider.id === promotionOffer?.value?.package.packageId;
		});
	}

	if (!provider || !provider.price) return null;

	return `${country.value === 'US' ? '$' : '£'}${provider.price} / month`;
});

const presentationTypeLabel = computed(() => {
	switch (promotionOffer?.value?.presentationType) {
		case PresentationType.Hd:
		// @ts-ignore
		case 'hd':
			return 'HD';
		case PresentationType['4K']:
			return '4K';
		case PresentationType.Dvd:
			return 'DVD';
		case PresentationType.Bluray:
			return 'Blu-Ray';
		default:
			return '';
	}
});

const onVisibilityChanged = (isVisible: boolean) => {
	if (!isVisible || !promotionOffer?.value) return;

	TrackingHelper.trackEvent(
		ImpressionTrackingEvents.OFFER,
		{
			action: 'impression',
			nonInteraction: true,
			value: OfferLocation.FREE_TRIAL,
			label: promotionOffer?.value.id === FALLBACK_PROMOTION_OFFER ? 'provider' : undefined,
		},
		[getOfferImpressionContext(promotionOffer?.value)]
	);
};

const observeVisibilityOptions = computed(() => ({
	...observableOptions,
	callback: (isVisible: boolean) => onVisibilityChanged(isVisible),
}));

const onClickOut = () => {
	trackTagEvent(promotionOffer);

	emit('clickOut', {
		buyboxOffer: promotionOffer?.value,
		offerLocation: OfferLocation.FREE_TRIAL,
	});
};

return { __sfc: true,ProviderIcon, t, tc, country, allProvidersById, props, emit, isArbitrating, withArbitrageTracking, trackTagEvent, inPromotionCatalogue, promotionOffer, catalogueItem, promotionCatalogue, playerIcon, mainText, subText, freeTrialText, clickoutLink, packageImg, ageRating, retailPrice, presentationTypeLabel, onVisibilityChanged, observeVisibilityOptions, onClickOut }
}

})